import { jsx as _jsx } from "react/jsx-runtime";
import { normalizeStyle } from '../server/layout/Box/Box';
/** `min` as a CSS length, in a box `width` wide (`100%` in the grid itself,
 *  `100cqi` where a size container stands in for it) with `gap` between
 *  columns. */
function minValue(min, gap, width) {
    if (typeof min === 'number')
        return `${min}px`;
    if (min === 'md-two-up') {
        // Two columns' share, held between the two-up floor and md: below
        // 2 × floor + gap it is the floor (one column), past 2 × md + gap it is
        // md (md's own count), and between them exactly two columns fit.
        return `clamp(var(--card-grid-min-two-up), (${width} - var(--space-${gap})) / 2, var(--card-grid-min-md))`;
    }
    return `var(--card-grid-min-${min})`;
}
/** The track list of a CardGrid without fixed `cols`: as many columns of at
 *  least `min` as fit, `gap` apart, sharing the leftover width. */
export function cardGridColumns(min, gap) {
    // `min(100%, …)` lets a single column shrink below `minValue` when the
    // container is narrower than it (mobile / clipped column) instead of
    // overflowing and getting clipped. On wide containers it equals
    // `minValue`, so multi-column behavior is unchanged.
    return `repeat(auto-fill, minmax(min(100%, ${minValue(min, gap, '100%')}), 1fr))`;
}
/** Columns counted up to; past it a count stays here. 12 columns of the
 *  smallest named tier (sm, 12rem) need a 155rem-wide box. */
const MAX_COUNTED_COLUMNS = 12;
/**
 * How many columns `cardGridColumns(min, gap)` lays out, with `gap` between them,
 * across the inline size of the nearest size container (100cqi), as a CSS
 * <number>: what CardRail sizes its cards by to match a grid's columns.
 *
 * auto-fill fits k columns when k·min + (k−1)·gap ≤ width, i.e. when
 * width + gap ≥ k·(min + gap). CSS cannot divide a length by a length in every
 * browser yet (typed arithmetic), so the count is one column plus one for each
 * further k that fits: clamp(0, sign(width + gap − k·(min + gap)) + 1, 1) is 1
 * when it fits (≥) and 0 when it does not.
 */
export function cardGridColumnCount(min, gap) {
    const step = `(${minValue(min, gap, '100cqi')} + var(--space-${gap}))`;
    const fits = Array.from({ length: MAX_COUNTED_COLUMNS - 1 }, (_, i) => `clamp(0, sign(100cqi + var(--space-${gap}) - ${i + 2} * ${step}) + 1, 1)`);
    return `calc(1 + ${fits.join(' + ')})`;
}
export function CardGrid({ cols, min = 'md', gap = 'lg', children, style, }) {
    const resolvedGap = `var(--space-${gap})`;
    const gridTemplateColumns = cols !== undefined ? `repeat(${cols}, minmax(0, 1fr))` : cardGridColumns(min, gap);
    return (_jsx("div", { style: {
            display: 'grid',
            gridTemplateColumns,
            rowGap: resolvedGap,
            columnGap: resolvedGap,
            ...normalizeStyle(style),
        }, children: children }));
}
//# sourceMappingURL=CardGrid.js.map